import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  Signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HorizontalScrollMenuModule } from 'ngx-horizontal-scroll-menu';

interface TabItem {
  id: string;
  title: string;
}

@Component({
  selector: 'sc-tab-bar',
  standalone: true,
  imports: [CommonModule, HorizontalScrollMenuModule],
  templateUrl: './tab-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabBarComponent implements AfterViewInit {
  @Input({ required: true }) tabList!: Signal<TabItem[]>;
  @Input({ required: true }) selectedTab!: Signal<TabItem | undefined>;
  @Input() onTabSelected?: (id: string) => void;

  @ViewChild('scrollMenu', { read: ElementRef }) scrollMenu!: ElementRef;
  @ViewChild('mobileScrollMenu', { read: ElementRef }) mobileScrollMenu!: ElementRef;

  public distance = 200;

  get selected() {
    return this.selectedTab();
  }

  ngAfterViewInit() {
    this.highlightSelectedTab();
  }

  private highlightSelectedTab(selected?: TabItem) {
    if (!this.scrollMenu) {
      setTimeout(() => this.highlightSelectedTab(selected), 0);
      return;
    }

    const list = this.scrollMenu.nativeElement.querySelector('ul#list-items');
    if (!list) return;

    const tabs = Array.from(list.querySelectorAll('li')) as HTMLElement[];
    this.updateTabClasses(tabs, selected ?? this.selectedTab());
  }

  private highlightMobileTab(selected?: TabItem) {
    if (!this.mobileScrollMenu?.nativeElement) return;

    const buttons = Array.from(
      this.mobileScrollMenu.nativeElement.querySelectorAll('button')
    ) as HTMLElement[];

    this.updateTabClasses(buttons, selected ?? this.selectedTab());
  }

  private updateTabClasses(elements: HTMLElement[], selected?: TabItem) {
    elements.forEach((el) => el.classList.remove('active-tab', 'tab-base'));
    elements.forEach((el) => el.classList.add('tab-base'));

    if (!selected) return;

    const activeEl = elements.find((el) => el.textContent?.trim() === selected.title?.trim());
    if (activeEl) {
      activeEl.classList.add('active-tab');
      this.adjustScroll(activeEl);
    }
  }

  private adjustScroll(activeTab: HTMLElement) {
    const container = activeTab.parentElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();

    const scrollLeft =
      container.scrollLeft +
      (tabRect.left - containerRect.left) -
      container.offsetWidth / 2 +
      tabRect.width / 2;

    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });
  }

  selectTab(tab: TabItem) {
    this.onTabSelected?.(tab.id);
    this.highlightSelectedTab(tab);
  }

  selectTabMobile(tab: TabItem) {
    this.onTabSelected?.(tab.id);
    this.highlightMobileTab(tab);

    if (!this.mobileScrollMenu) return;
    const container = this.mobileScrollMenu.nativeElement;

    const buttons = Array.from(container.querySelectorAll('button')) as HTMLElement[];
    const activeTab = buttons.find((btn) => btn.textContent?.trim() === tab.title);
    if (!activeTab) return;

    buttons.forEach((btn) => btn.classList.remove('active-tab'));
    activeTab.classList.add('active-tab');

    this.adjustScroll(activeTab);
  }
}
